import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

const todayKey = () => new Date().toLocaleDateString("en-CA");

/**
 * Trial-countdown amber banner shown when the user is on a `trialing` sub.
 * Goes amber when ≤ 3 days remain. CTA routes to admin billing to upgrade.
 */
export function TrialBanner({ trial }) {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => localStorage.getItem("chalk_trial_banner_dismissed") === todayKey());
  if (!trial || hidden) return null;
  const isUrgent = trial.trial_days_left <= 3;
  return (
    <div
      data-testid="trial-banner"
      role="status"
      style={{
        marginTop: 14, padding: 12, borderRadius: 12,
        background: isUrgent ? "var(--chalk-amber-bg)" : "#FBF8F2",
        border: `0.5px solid ${isUrgent ? "#E9D2A6" : "var(--chalk-border)"}`,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: "var(--chalk-board)", fontWeight: 500 }}>
          {trial.trial_days_left === 0
            ? "Chalkboard trial — ends today"
            : `Chalkboard trial — ${trial.trial_days_left} day${trial.trial_days_left === 1 ? "" : "s"} left`}
        </div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
          Season $29 · Monthly $11.99 · Annual $119
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button
        data-testid="trial-upgrade"
        onClick={() => navigate("/app/upgrade")}
        style={{
          background: "var(--chalk-board)", color: "var(--chalk-white)",
          border: "none", borderRadius: 999, padding: "6px 14px",
          fontSize: 12, cursor: "pointer",
        }}
      >
        Upgrade
      </button>
      <button data-testid="trial-banner-dismiss" aria-label="Dismiss for today"
        onClick={() => { localStorage.setItem("chalk_trial_banner_dismissed", todayKey()); setHidden(true); }}
        style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--chalk-muted)", padding: 4 }}>
        <X size={14} />
      </button>
      </div>
    </div>
  );
}

/**
 * Expired/cancelled-subscription amber banner with a "Renew Chalkboard" CTA
 * that opens Stripe Checkout via the onRenew prop.
 */
export function ExpiredBanner({ expired, renewing, onRenew }) {
  if (!expired) return null;
  return (
    <div
      data-testid="expired-banner"
      role="status"
      style={{
        marginTop: 14, padding: 12, borderRadius: 12,
        background: "var(--chalk-amber-bg)",
        border: "0.5px solid #E9D2A6",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: "var(--chalk-board)", fontWeight: 500 }}>
          {expired.status === "expired" ? "Trial expired." : "Subscription cancelled."}
        </div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
          You're on Pickup. Renew to bring back The Chalkboard.
        </div>
      </div>
      <button
        data-testid="renew-chalkboard-btn"
        onClick={onRenew}
        disabled={renewing}
        style={{
          background: "var(--chalk-board)", color: "var(--chalk-white)",
          border: "none", borderRadius: 999, padding: "6px 14px",
          fontSize: 12, cursor: renewing ? "wait" : "pointer", opacity: renewing ? 0.7 : 1,
        }}
      >
        {renewing ? "Renewing…" : "Renew Chalkboard"}
      </button>
    </div>
  );
}
